import { scope } from '#components/generics/js/selectors/scope.js'
import listener from '@mailobj-browser/front/js/events/listeners/listener.js'
import passive from '@mailobj-browser/front/js/events/options/passive.js'
import click from '@mailobj-browser/front/js/events/types/click.js'
import transitionEnd
  from '@mailobj-browser/front/js/events/types/transitionEnd.js'
import all from '@mailobj-browser/front/js/selectors/all.js'
import one from '@mailobj-browser/front/js/selectors/one.js'
import inserted from '@mailobj-browser/front/js/tree/inserted.js'
import replaceChildren from '@mailobj-browser/front/js/tree/replaceChildren.js'
import object from '@mailobj-browser/front/js/utils/object.js'
import * as utilities from '#utilities/js/styles.js'
import { elements, modifiers } from '../styles.js'
import stopImmediatePropagation from '@mailobj-browser/front/js/events/hooks/stopImmediatePropagation.js'
import array from '@mailobj-browser/front/js/utils/array.js'
import { syncExpandedState } from '../nav/expanded.js'
import * as sharedClasses from '#utilities/js/ea/sharedClasses.js'

const onClick = object(listener, {
  type: click,
  hooks: array([
    stopImmediatePropagation
  ]),
  task: (
    button
  ) => {
    queueMicrotask(async () => {
      await syncExpandedState(button)

      const { ownerDocument, dataset } = button
      const { body } = ownerDocument
      const { target } = dataset
      const panel = one(`[data-panel-uid='${target}']`, ownerDocument)

      panel.classList.toggle(modifiers.collapse_panel.open)

      if (panel.classList.contains(modifiers.has.transition_end)) {
        if (panel.classList.contains(utilities.modifiers.visibility.hidden)) {
          panel.classList.remove(utilities.modifiers.visibility.hidden)

          if (getComputedStyle(panel).getPropertyValue('--ea_collapse_panel_is_fullscreen') === 'true') {
            sharedClasses.add(panel, utilities.modifiers.overflow.hidden_until_sm)
          }
        }
      } else {
        panel.classList.toggle(utilities.modifiers.visibility.hidden)
        panel.classList.toggle(utilities.modifiers.display.none)
        if (getComputedStyle(panel).getPropertyValue('--ea_collapse_panel_is_fullscreen') === 'true') {
          if (body.classList.contains(utilities.modifiers.overflow.hidden_until_sm)) {
            sharedClasses.remove(panel, utilities.modifiers.overflow.hidden_until_sm)
          } else {
            sharedClasses.add(panel, utilities.modifiers.overflow.hidden_until_sm)
          }
        }
      }

      manageButton(panel, target)
    })
  }
})

export const manageButton = ((panel, target) => {
  const { ownerDocument } = panel
  const buttons = all(`[data-target='${target}']`, ownerDocument)

  for (const button of buttons) {
    const { open, close, titleOpen, titleClose } = scope(button)
    const text = one(`.${elements.button_text}`, button)

    // is close, is about to get opened
    if (panel.classList.contains(modifiers.collapse_panel.open)) {
      if (text && close) {
        replaceChildren(text, close)
      }

      if (button.title && button.title !== '') {
        if (titleClose) {
          button.title = titleClose
        } else if (close) {
          button.title = close
        }
      }

      button.classList.add(modifiers.collapse_panel.open)
      button.setAttribute('aria-expanded', 'true')
    } else {
      if (text && open) {
        replaceChildren(text, open)
      }

      if (button.title && button.title !== '') {
        if (titleOpen) {
          button.title = titleOpen
        } else if (open) {
          button.title = open
        }
      }

      button.classList.remove(modifiers.collapse_panel.open)
      button.setAttribute('aria-expanded', 'false')
    }
  }
})

const onTransitionEnd = object(listener, {
  type: transitionEnd,
  passive,
  task: (
    panel
  ) => {
    if (panel.classList.contains(modifiers.has.transition_end) &&
      !panel.classList.contains(modifiers.collapse_panel.open)) {
      panel.classList.add(utilities.modifiers.visibility.hidden)
      sharedClasses.remove(panel, utilities.modifiers.overflow.hidden_until_sm)
    }
  }
})

export default button => {
  queueMicrotask(async () => {
    const { ownerDocument } = button
    const { body } = ownerDocument
    await inserted(button, body)

    const { dataset } = button
    const { target } = dataset
    const panel = one(`[data-panel-uid='${target}']`, ownerDocument)

    onClick.listen(button)

    if (Object.hasOwn(globalThis, 'ontransitionend')) {
      onTransitionEnd.listen(panel)
    }
  })
}
